import React, { useState, useEffect, useCallback } from 'react';
import { SidebarProvider, SidebarInset, SidebarTrigger } from '@/components/ui/sidebar';
import { AppSidebar } from './AppSidebar';
import { useIsMobile } from '@/hooks/use-mobile';
import { MobileBottomNav } from '@/components/ui/mobile-bottom-nav';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Eye } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { CoinWalletChip } from '@/components/economy/CoinWalletChip';
import { CommandPalette, CommandPaletteTrigger } from './CommandPalette';
import { UserMenu } from './UserMenu';
import { Button } from '@/components/ui/button';
import { Search } from 'lucide-react';
import { PsyforgeMark } from '@/components/brand/PsyforgeMark';
import { NetworkStatusBanner } from '@/components/ui/NetworkStatusBanner';
import { AnimatePresence, motion } from 'framer-motion';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { useNavigationPreload } from '@/hooks/useNavigationPreload';
import { useDailyCheckin } from '@/hooks/useDailyCheckin';
import { useReflectionReminder } from '@/hooks/useReflectionReminder';
import { useRouteTracker } from '@/hooks/useRouteTracker';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const { isImpersonating, impersonatedUser } = useImpersonation();
  const [commandOpen, setCommandOpen] = useState(false);
  
  // Prefetch cross-feature data based on current route
  useNavigationPreload();

  // Mint daily check-in Coin reward (once per UTC day per user)
  useDailyCheckin();

  // Schedule opt-in browser nudge for the daily reflection
  useReflectionReminder();

  // Track per-feature last-visit timestamps for Home explorer status pills
  useRouteTracker();

  // Cmd+K / Ctrl+K listener
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCommandOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Add padding when impersonation banner is visible
  const impersonationPadding = isImpersonating ? 'pt-12' : '';

  const pageTransition = {
    initial: { opacity: 0, y: 6 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: 6 },
    transition: { duration: 0.15, ease: 'easeOut' as const },
  };

  if (isMobile) {
    return (
      <>
        <NetworkStatusBanner />
        <CommandPalette open={commandOpen} onOpenChange={setCommandOpen} />
        <div className={`min-h-dvh bg-gradient-to-b from-background via-background to-muted/20 pb-24 overflow-x-hidden ${impersonationPadding}`}>
          {/* Mobile top bar — Astral shell */}
          <header
            className="sticky top-0 z-40 min-h-14 flex items-center justify-between gap-2 px-3 border-b border-border/40 bg-background/80 backdrop-blur-xl"
            style={{ paddingTop: 'env(safe-area-inset-top)' }}
          >
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 rounded-lg px-1 py-1 -ml-1 active:opacity-70 transition"
              aria-label="Home"
            >
              <PsyforgeMark className="h-7 w-7" />
              <span className="font-display text-base font-medium tracking-tight text-foreground">
                psyforge
              </span>
            </button>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setCommandOpen(true)}
                aria-label="Search"
                className="h-9 w-9"
              >
                <Search className="h-4 w-4" />
              </Button>
              <CoinWalletChip />
              <NotificationBell />
              <UserMenu />
            </div>
          </header>
          <div className="px-1">
          <AnimatePresence mode="wait">
            <motion.div key={location.pathname} {...pageTransition}>
              {children}
            </motion.div>
          </AnimatePresence>
          </div>
        </div>
        <MobileBottomNav />
      </>
    );
  }

  return (
    <SidebarProvider defaultOpen={true}>
      <NetworkStatusBanner />
      <CommandPalette open={commandOpen} onOpenChange={setCommandOpen} />
      <div className={`flex min-h-dvh w-full bg-gradient-to-b from-background via-background to-muted/20 ${impersonationPadding}`}>
        <AppSidebar />
        <SidebarInset className="flex-1">
          <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border/50 px-4 sticky top-0 bg-background/95 backdrop-blur-xl z-30">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />

            {isImpersonating && impersonatedUser && (
              <Badge className="bg-amber-500/20 text-amber-600 border-amber-500/30 gap-1">
                <Eye className="h-3 w-3" />
                Viewing as {impersonatedUser.display_name || impersonatedUser.email}
              </Badge>
            )}
            
            <div className="ml-auto flex items-center gap-1">
              <CommandPaletteTrigger onClick={() => setCommandOpen(true)} />
              <CoinWalletChip />
              <NotificationBell />
              <UserMenu />
            </div>
          </header>
          
          <main className="flex-1 p-6">
            <AnimatePresence mode="wait">
              <motion.div key={location.pathname} {...pageTransition}>
                {children}
              </motion.div>
            </AnimatePresence>
          </main>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
};
